import { useAppDispatch, useAppSelector } from 'app/hooks';
import { syncDuctedNewSystemSlots } from 'features/configurations';
import useSystemsAwaitingFirstSave from 'hooks/use-systems-awaiting-first-save';
import { useEffect, useMemo } from 'react';
import {
  selectActiveDuctedDesign,
  selectDuctedPairLinkMode
} from 'utils/ducted-design';
import {
  copyDuctedSlotPreferences,
  planDuctedSlotPreferenceSeeds
} from 'utils/ducted-slot-preferences';

type DuctedNewSystemSlotsWatcherProps = {
  loading: boolean;
};

export default function DuctedNewSystemSlotsWatcher({
  loading
}: DuctedNewSystemSlotsWatcherProps) {
  const dispatch = useAppDispatch();
  const linkMode = useAppSelector(selectDuctedPairLinkMode);
  const design = useAppSelector(selectActiveDuctedDesign);
  const {
    existingSystems,
    recommendationSelectionsBySystem,
    wantCompositionPicksBySystem,
    equipmentPersistVersion
  } = useAppSelector((state) => state.structure);
  const isAwaitingFirstSave = useSystemsAwaitingFirstSave();
  const active = !loading && linkMode === 'automatic' && design !== null;

  useEffect(() => {
    if (!active) return;
    dispatch(syncDuctedNewSystemSlots());
  }, [active, design, existingSystems, dispatch]);

  const seeds = useMemo(
    () =>
      planDuctedSlotPreferenceSeeds({
        existingSystems,
        recommendationSelectionsBySystem,
        wantCompositionPicksBySystem,
        isAwaitingFirstSave
      }),
    [
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem,
      isAwaitingFirstSave
    ]
  );

  useEffect(() => {
    if (!active) return;
    seeds.forEach((seed) => {
      dispatch(copyDuctedSlotPreferences(seed));
    });
  }, [active, seeds, equipmentPersistVersion, dispatch]);

  return null;
}
